import {
  Archive,
  ChevronLeft,
  ChevronRight,
  Clock,
  MailCheck,
  MailOpen,
  Paperclip,
  Search,
  Square,
  SquareCheck,
  SquareMinus,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import type {
  AgentRef,
  ComposeDraftSummary,
  Conversation,
  Inbox,
} from '../../../api/types';
import { snippetOf } from '../../../lib/commsFormat';
import { labelTint } from '../../../lib/labelTint';
import { useTimeFormat } from '../../../lib/tenantTime';
import { RefreshButton } from '../../../components/RefreshButton';
import { ReservationChip } from '../booking/ReservationChip';
import { composeDraftRecipient, composeDraftSnippet } from '../composeDrafts';
import { useComms } from '../context';
import { useDraftSummaries } from '../drafts';
import { ContactRoleChip } from '../DispatchRoleChip';
import { contactDisplayName } from '../dispatchIdentity';
import { ConversationRowsSkeleton } from '../Skeletons';
import { useInfiniteScroll } from '../useInfiniteScroll';
import type { MailActions } from './actions';
import { useLatestBookings, useSubjectBookings } from './latestBookings';

/** Label chips drawn on a row before the rest fold into a count. */
const ROW_LABELS = 3;

/**
 * The list half of the mail-client layout: one line per conversation, the row
 * actions on hover, and a selection that puts the same actions in the toolbar.
 *
 * Deliberately NOT a variant of ConversationList. That component draws a
 * three-line card sized for a 350px column beside an open thread; this one is a
 * single line across the full width, and every decision below — where the date
 * sits, what a hover reveals, why unread is the loud state rather than the
 * annotated one — follows from that width rather than from a flag.
 */
export function GmailList({
  rows,
  composeDrafts = [],
  onOpenComposeDraft,
  inboxById,
  agents,
  loading,
  stale,
  refreshing,
  error,
  hasMore,
  loadingMore,
  onLoadMore,
  onOpen,
  actions,
  search,
  onSearch,
}: {
  rows: Conversation[];
  /**
   * The Drafts view only: unsent NEW messages, which have no conversation to be
   * rows of. They carry no checkbox, star or hover actions — archive, snooze and
   * the bin are conversation verbs, and this is a message that has not become
   * one yet.
   */
  composeDrafts?: readonly ComposeDraftSummary[];
  onOpenComposeDraft?: (draft: ComposeDraftSummary) => void;
  inboxById: Map<number, Inbox>;
  agents: AgentRef[];
  loading: boolean;
  stale: boolean;
  refreshing: boolean;
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  onOpen: (conversation: Conversation) => void;
  actions: MailActions;
  search: string;
  onSearch: (next: string) => void;
}) {
  const { listTime } = useTimeFormat();
  const { source } = useComms();
  const drafts = useDraftSummaries();
  // One request per page of rows, not one per row — see latestBookings.ts.
  const lastReservations = useLatestBookings(source, rows);
  // A subject that names a reservation outranks the last one — see ConversationList.
  const subjectReservations = useSubjectBookings(source, rows);
  const [picked, setPicked] = useState<ReadonlySet<number>>(new Set());
  const sentinel = useInfiniteScroll({ hasMore, busy: loadingMore, onLoadMore });

  // A selection outlives neither a filter change nor a delete, so it is pruned
  // against the rows actually on screen rather than trusted.
  const live = useMemo(() => {
    const ids = new Set(rows.map((row) => row.id));
    return [...picked].filter((id) => ids.has(id));
  }, [picked, rows]);

  const allPicked = rows.length > 0 && live.length === rows.length;
  const somePicked = live.length > 0 && !allPicked;
  const clear = () => setPicked(new Set());
  // Gmail's one envelope button, which reads the selection rather than toggling
  // a mode: anything unread in it and the button offers to read them, because
  // that is what somebody who just selected an unread batch means to do.
  const pickedUnread = live.some((id) => (rows.find((row) => row.id === id)?.unreadCount ?? 0) > 0);

  const toggle = (id: number) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** Run one action over the selection, then drop it — the rows are gone. */
  const bulk = (run: (id: number) => void) => {
    for (const id of live) run(id);
    clear();
  };

  return (
    <div className="gm-list">
      <div className="gm-toolbar">
        <button
          type="button"
          className="gm-icon gm-check"
          title={allPicked ? 'Deselect all' : 'Select all'}
          onClick={() => setPicked(allPicked ? new Set() : new Set(rows.map((row) => row.id)))}
        >
          {allPicked ? <SquareCheck size={19} /> : somePicked ? <SquareMinus size={19} /> : <Square size={19} />}
        </button>
        {live.length > 0 ? (
          <>
            {/* Gmail's own order, and the same verbs ours can actually keep:
                archive is our Resolve, the bin is the internal delete. */}
            <button
              type="button"
              className="gm-icon"
              title="Archive (resolve)"
              onClick={() => bulk(actions.archive)}
            >
              <Archive size={19} />
            </button>
            <button
              type="button"
              className="gm-icon"
              title="Delete"
              onClick={() => bulk(actions.remove)}
            >
              <Trash2 size={19} />
            </button>
            <button
              type="button"
              className="gm-icon"
              title={pickedUnread ? 'Mark as read' : 'Mark as unread'}
              onClick={() => bulk(pickedUnread ? actions.markRead : actions.markUnread)}
            >
              {pickedUnread ? <MailCheck size={19} /> : <MailOpen size={19} />}
            </button>
            <button
              type="button"
              className="gm-icon"
              title="Snooze until tomorrow"
              onClick={() => bulk((id) => actions.snooze(id, 24))}
            >
              <Clock size={19} />
            </button>
            <span className="gm-picked">{live.length} selected</span>
            <button type="button" className="gm-icon" title="Clear selection" onClick={clear}>
              <X size={18} />
            </button>
          </>
        ) : (
          <>
            <RefreshButton
              className="gm-icon"
              size={19}
              label={null}
              title="Refresh"
              refreshing={refreshing}
              onClick={actions.reload}
            />
            <div className="gm-search">
              <Search size={15} />
              <input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="Search mail"
              />
              {search && (
                <button type="button" title="Clear search" onClick={() => onSearch('')}>
                  <X size={13} />
                </button>
              )}
            </div>
          </>
        )}
        <div className="gm-toolbar-fill" />
        {/* Gmail's "1–50 of 312". Ours counts what is loaded rather than a
            total the list read does not carry, so it says so plainly rather
            than inventing a denominator. */}
        <span className="gm-range">
          {rows.length > 0 ? `1–${rows.length}` : '0'}
          {hasMore ? ' of many' : ''}
        </span>
        <button
          type="button"
          className="gm-icon"
          title="Newer"
          disabled
          aria-label="Newer conversations"
        >
          <ChevronLeft size={19} />
        </button>
        <button
          type="button"
          className="gm-icon"
          title={hasMore ? 'Older' : 'No older conversations'}
          disabled={!hasMore || loadingMore}
          onClick={onLoadMore}
        >
          <ChevronRight size={19} />
        </button>
      </div>

      {refreshing && !stale ? <div className="comms-progress" /> : <div className="comms-progress-slot" />}

      <div className="gm-rows">
        {error && <div className="cv-rows-error">{error}</div>}
        {/* Above the conversation rows and outside the skeleton: these are
            already loaded and live in this browser, so blanking them while the
            server answers for the other half would make a draft the agent just
            saved flicker out of the list they saved it into. */}
        {composeDrafts.map((draft) => (
          <div key={`compose-${draft.id}`} className="gm-row gm-row-compose">
            {/* Two empty cells where the checkbox and the star are, so the text
                of every row in the list still starts at the same x. */}
            <span className="gm-icon" aria-hidden />
            <span className="gm-icon" aria-hidden />
            <button
              type="button"
              className="gm-row-main"
              onClick={() => onOpenComposeDraft?.(draft)}
            >
              <span className="gm-row-from">
                <span className="gm-row-name">{composeDraftRecipient(draft)}</span>
                {inboxById.get(draft.inboxId) && (
                  <span className="gm-row-box">{inboxById.get(draft.inboxId)?.name}</span>
                )}
              </span>
              <span className="gm-row-text">
                <span className="gm-row-subject">{draft.subject?.trim() || '(no subject)'}</span>
                <span className="gm-row-snippet">
                  {' — '}
                  <span className="gm-row-draft">Unsent</span>
                  {composeDraftSnippet(draft)}
                </span>
              </span>
              {draft.attachmentCount > 0 && <Paperclip size={13} className="gm-row-clip" />}
              <span className="gm-row-date">{listTime(draft.updatedAt)}</span>
            </button>
          </div>
        ))}
        {(loading && stale) || (loading && rows.length === 0) ? (
          <ConversationRowsSkeleton />
        ) : rows.length === 0 ? (
          composeDrafts.length === 0 && (
            <div className="gm-empty">
              <MailOpen size={26} />
              <div>Nothing here.</div>
              <span>{search ? 'No mail matches that search.' : 'This view is empty.'}</span>
            </div>
          )
        ) : (
          rows.map((row) => {
            const unread = (row.unreadCount ?? 0) > 0;
            const inbox = inboxById.get(row.inboxId);
            const draft = drafts.get(row.id);
            const subject =
              typeof row.additionalAttributes.mailSubject === 'string' &&
              row.additionalAttributes.mailSubject.trim()
                ? row.additionalAttributes.mailSubject
                : '(no subject)';
            const assignee = row.assigneeIsBot
              ? 'Bot'
              : (agents.find((a) => a.dashBoardId === row.assigneeDashBoardId)?.name ?? null);
            const named = subjectReservations.get(row.id) ?? null;
            const lastReservation = named?.booking ?? lastReservations.get(row.contact.id);
            return (
              // A press anywhere on the line that is not one of its controls
              // opens the thread, the way a mail client's row does. The main
              // button is the same action for the keyboard; this is what lets
              // the trailing half (the date, the assignee) stay a target now
              // that it sits beside that button rather than inside it.
              <div
                key={row.id}
                className={`gm-row${unread ? ' unread' : ''}${picked.has(row.id) ? ' picked' : ''}`}
                onClick={(event) => {
                  if (!(event.target as HTMLElement).closest('button, a')) onOpen(row);
                }}
              >
                <button
                  type="button"
                  className="gm-icon gm-row-check"
                  title={picked.has(row.id) ? 'Deselect' : 'Select'}
                  onClick={() => toggle(row.id)}
                >
                  {picked.has(row.id) ? <SquareCheck size={16} /> : <Square size={16} />}
                </button>
                <button
                  type="button"
                  className={`gm-icon gm-row-star${row.starred ? ' on' : ''}`}
                  title={row.starred ? 'Remove star' : 'Star'}
                  onClick={() => actions.toggleStar(row.id, !row.starred)}
                >
                  <Star size={16} style={row.starred ? { fill: 'currentColor' } : undefined} />
                </button>
                {/* The sender and the subject are one button, which opens the
                    thread — the controls around it are siblings rather than
                    children, which is what keeps them legal: a button cannot
                    hold a button, and the reservation is one. */}
                <button type="button" className="gm-row-main" onClick={() => onOpen(row)}>
                  <span className="gm-row-from">
                    <span className="gm-row-name">{contactDisplayName(row.contact)}</span>
                    <ContactRoleChip contact={row.contact} />
                    {inbox && <span className="gm-row-box">{inbox.name}</span>}
                  </span>
                  <span className="gm-row-text">
                    {/* Gmail's place for them: before the subject, each in its
                        own colour, so a labelled thread is found by eye. A row
                        has room for a few; the rest are a count and a hover. */}
                    {row.labels.slice(0, ROW_LABELS).map((label) => (
                      <span key={label} className="gm-row-label" style={labelTint(label)} title={label}>
                        {label}
                      </span>
                    ))}
                    {row.labels.length > ROW_LABELS && (
                      <span
                        className="gm-row-label gm-row-label-more"
                        title={row.labels.slice(ROW_LABELS).join(', ')}
                      >
                        +{row.labels.length - ROW_LABELS}
                      </span>
                    )}
                    <span className="gm-row-subject">{subject}</span>
                    {/* The separator lives in the snippet rather than between
                        two elements: JSX drops whitespace across a newline, so
                        a space written there would not survive the build and
                        the subject would run into its own preview. */}
                    <span className="gm-row-snippet">
                      {draft ? (
                        <>
                          {' — '}
                          <span className="gm-row-draft">Draft</span>
                          {draft.preview}
                        </>
                      ) : (
                        ` — ${snippetOf(row.lastMessage?.content ?? null)}`
                      )}
                    </span>
                  </span>
                </button>
                <div className="gm-row-tail">
                  {lastReservation && <ReservationChip booking={lastReservation} base="gm-row-trip" named={named} />}
                  {assignee && <span className="gm-row-assignee">{assignee}</span>}
                  {(row.lastMessage?.attachments?.length ?? 0) > 0 && (
                    <Paperclip size={13} className="gm-row-clip" />
                  )}
                  <span className="gm-row-date">{listTime(row.lastActivityAt)}</span>
                </div>
                {/* Hover actions sit OVER the date, as Gmail's do — the row is
                    already full at this width, and the date is the one thing
                    that is redundant while the pointer is on the row. */}
                <div className="gm-row-actions">
                  <button
                    type="button"
                    className="gm-icon"
                    title="Archive (resolve)"
                    onClick={() => actions.archive(row.id)}
                  >
                    <Archive size={16} />
                  </button>
                  <button
                    type="button"
                    className="gm-icon"
                    title="Delete"
                    onClick={() => actions.remove(row.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                  <button
                    type="button"
                    className="gm-icon"
                    title={unread ? 'Mark as read' : 'Mark as unread'}
                    onClick={() => (unread ? actions.markRead(row.id) : actions.markUnread(row.id))}
                  >
                    <MailOpen size={16} />
                  </button>
                  <button
                    type="button"
                    className="gm-icon"
                    title="Snooze until tomorrow"
                    onClick={() => actions.snooze(row.id, 24)}
                  >
                    <Clock size={16} />
                  </button>
                </div>
              </div>
            );
          })
        )}
        {hasMore && <div ref={sentinel} className="gm-sentinel" />}
        {loadingMore && <div className="gm-more">Loading…</div>}
      </div>
    </div>
  );
}
